import { useEffect, useRef, useState } from 'react';
import { Search, Loader2, ShoppingBag, Inbox, ShoppingBasket, Package, Users } from 'lucide-react';
import { adminGlobalSearch } from '@/lib/api';

const SECTIONS = [
  { key: 'orders',    icon: ShoppingBag,    label: 'Orders',    tab: 'orders' },
  { key: 'inquiries', icon: Inbox,          label: 'Inquiries', tab: 'inquiries' },
  { key: 'quotes',    icon: ShoppingBasket, label: 'Quotes',    tab: 'quotes' },
  { key: 'customers', icon: Users,          label: 'Customers', tab: 'customers' },
  { key: 'products',  icon: Package,        label: 'Products',  tab: 'products' },
];

/**
 * Top-bar admin search — hits `/api/admin/search` and shows a grouped
 * dropdown of matches across orders, inquiries, quotes, customers and
 * products. Debounced so we don't hammer the backend on every keystroke.
 */
export default function GlobalSearch({ onNavigate }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const timerRef = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!q || q.trim().length < 2) {
      setResults(null);
      setLoading(false);
      return () => {};
    }
    setLoading(true);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      try {
        const res = await adminGlobalSearch(q.trim());
        setResults(res);
      } catch {
        setResults(null);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(timerRef.current);
  }, [q]);

  // Close dropdown on outside click
  useEffect(() => {
    const h = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const totalHits = results
    ? SECTIONS.reduce((s, sec) => s + (results[sec.key]?.length || 0), 0)
    : 0;

  const clickResult = (tab) => {
    setOpen(false);
    setQ('');
    onNavigate?.(tab);
  };

  return (
    <div ref={wrapRef} className="relative w-full max-w-md">
      <div className="relative">
        <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
        <input
          type="text"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Search orders, customers, products…"
          data-testid="admin-global-search"
          className="w-full bg-white/[0.03] hover:bg-white/[0.05] focus:bg-white/[0.06] border border-white/10 focus:border-white/25 outline-none pl-9 pr-4 py-2 text-sm text-white placeholder:text-white/35 transition-colors"
        />
        {loading && (
          <Loader2 size={12} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-white/40" />
        )}
      </div>
      {open && q.trim().length >= 2 && (
        <div
          data-testid="admin-search-results"
          className="absolute top-full left-0 right-0 mt-2 border border-white/15 bg-[var(--mw-bg)] shadow-2xl z-50 max-h-[70vh] overflow-y-auto"
        >
          {loading && !results && (
            <div className="p-6 text-center text-white/40 text-xs uppercase tracking-[0.22em]">Searching…</div>
          )}
          {!loading && totalHits === 0 && results && (
            <div className="p-6 text-center text-white/40 text-xs uppercase tracking-[0.22em]">No matches</div>
          )}
          {results && SECTIONS.map((sec) => {
            const rows = results[sec.key] || [];
            if (!rows.length) return null;
            const Icon = sec.icon;
            return (
              <div key={sec.key} className="border-t border-white/5 first:border-t-0">
                <div className="px-4 py-2 text-[9px] uppercase tracking-[0.28em] text-white/40 bg-white/[0.02] flex items-center gap-2">
                  <Icon size={11} /> {sec.label} · {rows.length}
                </div>
                {rows.map((r) => (
                  <button
                    key={`${sec.key}-${r.id}`}
                    type="button"
                    onClick={() => clickResult(sec.tab)}
                    className="w-full text-left px-4 py-2.5 hover:bg-white/[0.05] transition-colors border-t border-white/5 first:border-t-0"
                  >
                    <div className="text-white text-sm truncate">{r.label}</div>
                    {r.sublabel && (
                      <div className="text-[11px] text-white/45 truncate mt-0.5">{r.sublabel}</div>
                    )}
                  </button>
                ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
